import api from './client';

// =============================================================================
// RELATIONSHIP REQUESTS API (Spec 235)
// Submissive-initiation request mechanism — sub creates, Dom approves/declines.
// =============================================================================

export type RelationshipRequestType =
  | 'privilege'
  | 'partner'
  | 'negotiation'
  | 'switch_role'
  | 'collaring_advance'
  | 'keyholder';

export type RelationshipRequestStatus = 'pending' | 'approved' | 'declined' | 'withdrawn';

export interface RelationshipRequest {
  id: number;
  publicId: string;
  organizationId: number;
  requesterUserId: string;       // UUID public id (after camelCase transform)
  targetDominantUserId: string;  // UUID public id (after camelCase transform)
  requestType: RelationshipRequestType;
  payload: Record<string, unknown>;
  message?: string;
  status: RelationshipRequestStatus;
  resolvedAt?: string;
  resolvedByUserId?: string;
  declineMessage?: string;
  createdAt: string;
  updatedAt: string;
  // Joined display fields (returned by backend)
  requesterDisplayName?: string;
  targetDominantDisplayName?: string;
}

export const relationshipRequestsAPI = {
  /**
   * Sub creates a request toward a Dominant in the Hierarchy.
   * Body is camelCase — controller destructures targetDominantUserId, requestType, payload, message.
   */
  createRelationshipRequest: (
    orgId: string,
    data: {
      targetDominantUserId: string; // UUID — resolved to numeric on backend
      requestType: RelationshipRequestType;
      payload?: Record<string, unknown>;
      message?: string;
    }
  ) =>
    api.post<{ data: RelationshipRequest }>(
      `/organizations/${orgId}/relationship-requests`,
      data
    ),

  /**
   * Sub lists their own requests (all statuses).
   */
  getMyRelationshipRequests: (orgId: string) =>
    api.get<{ data: RelationshipRequest[] }>(
      `/organizations/${orgId}/relationship-requests/mine`
    ),

  /**
   * Sub withdraws a pending request. Sets status to 'withdrawn'.
   */
  withdrawRelationshipRequest: (orgId: string, requestId: string) =>
    api.delete<{ success: boolean }>(
      `/organizations/${orgId}/relationship-requests/${requestId}`
    ),

  /**
   * Dom lists pending requests addressed to them.
   */
  getPendingRelationshipRequests: (orgId: string) =>
    api.get<{ data: RelationshipRequest[] }>(
      `/organizations/${orgId}/relationship-requests/pending`
    ),

  /**
   * Dom approves a request. Executor runs inside a DB transaction on backend.
   */
  approveRelationshipRequest: (orgId: string, requestId: string) =>
    api.post<{ success: boolean }>(
      `/organizations/${orgId}/relationship-requests/${requestId}/approve`,
      {}
    ),

  /**
   * Dom declines a request. Optional decline message stored on the row.
   */
  declineRelationshipRequest: (
    orgId: string,
    requestId: string,
    data?: { declineMessage?: string }
  ) =>
    api.post<{ success: boolean }>(
      `/organizations/${orgId}/relationship-requests/${requestId}/decline`,
      data ?? {}
    ),
};
